import { useEffect, useRef, useState } from 'react';
import apiService from '../services/api';

const emptyForm = () => ({
  name: '',
  offer_id: '',
  offer_type: 'card_linked',
  preferred_order: 0,
  small_logo_url: '',
  large_logo_url: '',
  small_logo_file: null,
  large_logo_file: null,
  summary: '',
  is_active: true,
  is_global: false,
});

const typeLabel = (offerType) =>
  offerType === 'click' ? 'In-app' : 'In-person';

const PreferredPartnersSection = ({ schoolId, schoolName }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const smallLogoInputRef = useRef(null);
  const largeLogoInputRef = useRef(null);

  const loadPartners = async () => {
    if (!schoolId) {
      setPartners([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await apiService.getFeaturedOffers(schoolId, {
        is_preferred_partner: true,
      });
      const list = Array.isArray(data) ? data : data?.results || [];
      setPartners(
        list
          .filter((o) => o && o.is_preferred_partner)
          .slice()
          .sort((a, b) => (b.preferred_order || 0) - (a.preferred_order || 0))
      );
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          'Failed to load preferred partners'
      );
      setPartners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPartners();
  }, [schoolId]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (!event.target.closest('.pp-dropdown-menu')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(''), 5000);
    return () => clearTimeout(t);
  }, [success]);

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm());
    setError('');
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setError('');
    setShowForm(true);
  };

  const openEdit = (partner) => {
    setEditing(partner);
    setForm({
      name: partner.name || '',
      offer_id: partner.offer_id || '',
      offer_type: partner.offer_type || 'card_linked',
      preferred_order: partner.preferred_order || 0,
      small_logo_url: partner.small_logo_url || '',
      large_logo_url: partner.large_logo_url || '',
      small_logo_file: null,
      large_logo_file: null,
      summary: partner.summary || '',
      is_active: partner.is_active !== false,
      is_global: partner.school == null,
    });
    setError('');
    setShowForm(true);
    setActiveDropdown(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.offer_id.trim()) {
      setError('Name and offer ID are required');
      return;
    }
    try {
      setSaving(true);
      setError('');
      let small_logo_url = form.small_logo_url;
      let large_logo_url = form.large_logo_url;
      if (form.small_logo_file || form.large_logo_file) {
        const uploadResponse = await apiService.uploadOfferImages(
          form.small_logo_file,
          form.large_logo_file
        );
        if (uploadResponse.small_logo_url) small_logo_url = uploadResponse.small_logo_url;
        if (uploadResponse.large_logo_url) large_logo_url = uploadResponse.large_logo_url;
      }

      const payload = {
        name: form.name.trim(),
        offer_id: form.offer_id.trim(),
        offer_type: form.offer_type === 'click' ? 'click' : 'card_linked',
        preferred_order: Number(form.preferred_order) || 0,
        is_preferred_partner: true,
        top_featured: false,
        bottom_featured: false,
        top_order: 0,
        bottom_order: 0,
        small_logo_url,
        large_logo_url,
        summary: form.summary || '',
        is_active: form.is_active !== false,
        school: form.is_global ? null : schoolId,
      };

      if (editing) {
        await apiService.updateFeaturedOffer(editing.id, payload);
        setSuccess('Preferred partner updated successfully!');
      } else {
        await apiService.createFeaturedOffer(payload);
        setSuccess('Preferred partner added successfully!');
      }
      closeForm();
      await loadPartners();
    } catch (err) {
      const data = err?.response?.data;
      let message = null;
      if (data) {
        if (typeof data === 'string') message = data;
        else if (data.detail) message = data.detail;
        else if (data.error) message = data.error;
        else if (typeof data === 'object') {
          const parts = Object.entries(data).map(
            ([field, val]) => `${field}: ${Array.isArray(val) ? val.join(', ') : val}`
          );
          if (parts.length) message = parts.join(' • ');
        }
      }
      setError(message || err?.message || 'Failed to save preferred partner');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this preferred partner?')) return;
    try {
      await apiService.deleteFeaturedOffer(id);
      setSuccess('Preferred partner deleted successfully!');
      setActiveDropdown(null);
      await loadPartners();
    } catch (err) {
      setError(err?.message || 'Failed to delete preferred partner');
    }
  };

  const handleToggleActive = async (partner) => {
    try {
      await apiService.updateFeaturedOffer(partner.id, {
        is_active: !partner.is_active,
      });
      setSuccess(
        partner.is_active
          ? 'Preferred partner deactivated'
          : 'Preferred partner activated'
      );
      setActiveDropdown(null);
      await loadPartners();
    } catch (err) {
      setError(err?.message || 'Failed to update preferred partner');
    }
  };

  const offerIdLabel =
    form.offer_type === 'click' ? 'Wildfire Offer ID *' : 'Olive Offer ID *';

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
      }}
    >
      <h3
        style={{
          fontFamily: '"Baikal Book"',
          fontSize: '20px',
          fontWeight: 600,
          color: '#111827',
          marginBottom: '8px',
        }}
      >
        Preferred Partners{schoolName ? ` for ${schoolName}` : ''}
      </h3>
      <p
        style={{
          fontFamily: '"Baikal Book"',
          fontSize: '13px',
          color: '#6b7280',
          marginBottom: '24px',
        }}
      >
        Shown in the Shop Preferred Partners carousel. Each partner can be In-person
        (Olive) or In-app (Wildfire).
      </p>

      {error && !showForm && (
        <div
          style={{
            padding: '12px',
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '16px',
            fontFamily: '"Baikal Book"',
            fontSize: '14px',
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: '12px',
            backgroundColor: '#dcfce7',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            color: '#16a34a',
            marginBottom: '16px',
            fontFamily: '"Baikal Book"',
            fontSize: '14px',
          }}
        >
          {success}
        </div>
      )}

      <div
        style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          marginBottom: '16px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h4
          style={{
            fontFamily: '"Baikal Book"',
            fontSize: '18px',
            fontWeight: 600,
            marginBottom: '20px',
            color: '#111827',
          }}
        >
          Current Preferred Partners
        </h4>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>Loading…</div>
        ) : partners.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              color: '#6b7280',
              fontFamily: '"Baikal Book"',
            }}
          >
            No preferred partners for this school yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {partners.map((partner) => {
              const badges = [];
              badges.push({
                label: typeLabel(partner.offer_type),
                kind: 'type',
              });
              badges.push({
                label: `Order ${partner.preferred_order || 0}`,
                kind: 'order',
              });
              if (partner.school == null) badges.push({ label: 'Global', kind: 'global' });
              if (!partner.is_active) badges.push({ label: 'Inactive', kind: 'inactive' });
              return (
                <div
                  key={partner.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    gap: '16px',
                    opacity: partner.is_active ? 1 : 0.6,
                  }}
                >
                  {partner.large_logo_url && (
                    <img
                      src={partner.large_logo_url}
                      alt={partner.name}
                      style={{
                        width: '50px',
                        height: '50px',
                        objectFit: 'contain',
                        borderRadius: '4px',
                      }}
                    />
                  )}
                  {partner.small_logo_url && (
                    <img
                      src={partner.small_logo_url}
                      alt={`${partner.name} badge`}
                      style={{
                        width: '36px',
                        height: '36px',
                        objectFit: 'cover',
                        borderRadius: '999px',
                        border: '1px solid #e5e7eb',
                        backgroundColor: 'white',
                      }}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontFamily: '"Baikal Book"',
                        fontSize: '16px',
                        fontWeight: 500,
                        marginBottom: '4px',
                      }}
                    >
                      {partner.name}
                    </div>
                    <div
                      style={{
                        fontFamily: '"Baikal Book"',
                        fontSize: '12px',
                        color: '#6b7280',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <span>ID: {partner.offer_id}</span>
                      {badges.map(({ label, kind }) => {
                        const palette =
                          kind === 'inactive'
                            ? { bg: '#fee2e2', fg: '#991b1b' }
                            : kind === 'global'
                              ? { bg: '#ede9fe', fg: '#5b21b6' }
                              : kind === 'type'
                                ? { bg: '#e0f7ff', fg: '#0369a1' }
                                : { bg: '#f3f4f6', fg: '#374151' };
                        return (
                          <span
                            key={label}
                            style={{
                              padding: '2px 8px',
                              backgroundColor: palette.bg,
                              color: palette.fg,
                              borderRadius: '999px',
                              fontSize: '11px',
                              fontWeight: 500,
                            }}
                          >
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <div className="pp-dropdown-menu" style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() =>
                        setActiveDropdown(
                          activeDropdown === partner.id ? null : partner.id
                        )
                      }
                      style={{
                        padding: '4px',
                        backgroundColor: 'transparent',
                        color: '#6b7280',
                        border: 'none',
                        borderRadius: '4px',
                        fontFamily: '"Baikal Book"',
                        fontSize: '24px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        width: '32px',
                        height: '32px',
                      }}
                    >
                      ⋮
                    </button>
                    {activeDropdown === partner.id && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          marginTop: '4px',
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                          zIndex: 1000,
                          minWidth: '150px',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => openEdit(partner)}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: 'transparent',
                            color: '#374151',
                            border: 'none',
                            fontFamily: '"Baikal Book"',
                            fontSize: '14px',
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggleActive(partner)}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: 'transparent',
                            color: '#374151',
                            border: 'none',
                            borderTop: '1px solid #e5e7eb',
                            fontFamily: '"Baikal Book"',
                            fontSize: '14px',
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          {partner.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(partner.id)}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: 'transparent',
                            color: '#dc2626',
                            border: 'none',
                            borderTop: '1px solid #e5e7eb',
                            fontFamily: '"Baikal Book"',
                            fontSize: '14px',
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <button
          type="button"
          onClick={openCreate}
          style={{
            marginTop: '20px',
            padding: '12px 24px',
            backgroundColor: '#00C8FF',
            color: '#000000',
            border: 'none',
            borderRadius: '8px',
            fontFamily: '"Baikal Book"',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span style={{ fontSize: '18px', lineHeight: '1' }}>+</span>
          Add Preferred Partner
        </button>
      </div>

      {showForm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: 1002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeForm();
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '720px',
              maxHeight: 'calc(100vh - 48px)',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 18px',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <div
                style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '16px',
                  fontWeight: 900,
                  color: '#111827',
                }}
              >
                {editing ? 'Edit Preferred Partner' : 'Add Preferred Partner'}
              </div>
              <button
                type="button"
                onClick={closeForm}
                disabled={saving}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '20px',
                  lineHeight: 1,
                  padding: '6px 8px',
                  color: '#111827',
                  opacity: saving ? 0.6 : 1,
                }}
              >
                ×
              </button>
            </div>

            <form
              onSubmit={handleSave}
              style={{ padding: '24px', overflowY: 'auto', flex: 1, minHeight: 0 }}
            >
              {error && (
                <div
                  style={{
                    padding: '12px 14px',
                    backgroundColor: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    color: '#991b1b',
                    marginBottom: '20px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                  }}
                >
                  {error}
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                  }}
                >
                  Type *
                </label>
                <select
                  value={form.offer_type}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, offer_type: e.target.value }))
                  }
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    backgroundColor: 'white',
                  }}
                >
                  <option value="card_linked">In-person (Olive)</option>
                  <option value="click">In-app (Wildfire)</option>
                </select>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px',
                  marginBottom: '20px',
                }}
              >
                <div>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151',
                    }}
                  >
                    Name *
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      backgroundColor: 'white',
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151',
                    }}
                  >
                    {offerIdLabel}
                  </label>
                  <input
                    type="text"
                    value={form.offer_id}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, offer_id: e.target.value }))
                    }
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      backgroundColor: 'white',
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                  }}
                >
                  Summary
                </label>
                <textarea
                  value={form.summary}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, summary: e.target.value }))
                  }
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    resize: 'vertical',
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                  }}
                >
                  Display order
                </label>
                <input
                  type="number"
                  value={form.preferred_order}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      preferred_order: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    backgroundColor: 'white',
                  }}
                />
                <div
                  style={{
                    marginTop: '6px',
                    fontSize: '12px',
                    color: '#6b7280',
                    fontFamily: '"Baikal Book"',
                  }}
                >
                  Higher numbers appear first in the carousel.
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  marginBottom: '24px',
                }}
              >
                <div>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151',
                    }}
                  >
                    Large Logo
                  </label>
                  {(form.large_logo_file || form.large_logo_url) ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={
                          form.large_logo_file
                            ? URL.createObjectURL(form.large_logo_file)
                            : form.large_logo_url
                        }
                        alt="Large Logo"
                        style={{
                          width: '160px',
                          height: '160px',
                          objectFit: 'cover',
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          backgroundColor: 'white',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            large_logo_file: null,
                            large_logo_url: '',
                          }))
                        }
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div>
                      <input
                        ref={largeLogoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file && file.type.startsWith('image/')) {
                            setForm((prev) => ({ ...prev, large_logo_file: file }));
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        onClick={() => largeLogoInputRef.current?.click()}
                        style={{
                          padding: '10px 16px',
                          border: '1px dashed #d1d5db',
                          borderRadius: '8px',
                          backgroundColor: '#f9fafb',
                          fontFamily: '"Baikal Book"',
                          fontSize: '13px',
                          cursor: 'pointer',
                        }}
                      >
                        Upload large logo
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151',
                    }}
                  >
                    Small Logo
                  </label>
                  {(form.small_logo_file || form.small_logo_url) ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={
                          form.small_logo_file
                            ? URL.createObjectURL(form.small_logo_file)
                            : form.small_logo_url
                        }
                        alt="Small Logo"
                        style={{
                          width: '72px',
                          height: '72px',
                          objectFit: 'cover',
                          border: '1px solid #e5e7eb',
                          borderRadius: '999px',
                          backgroundColor: 'white',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            small_logo_file: null,
                            small_logo_url: '',
                          }))
                        }
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div>
                      <input
                        ref={smallLogoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file && file.type.startsWith('image/')) {
                            setForm((prev) => ({ ...prev, small_logo_file: file }));
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        onClick={() => smallLogoInputRef.current?.click()}
                        style={{
                          padding: '10px 16px',
                          border: '1px dashed #d1d5db',
                          borderRadius: '8px',
                          backgroundColor: '#f9fafb',
                          fontFamily: '"Baikal Book"',
                          fontSize: '13px',
                          cursor: 'pointer',
                        }}
                      >
                        Upload small logo
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={form.is_global}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, is_global: e.target.checked }))
                  }
                />
                Global (all schools)
              </label>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '24px',
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, is_active: e.target.checked }))
                  }
                />
                Active
              </label>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                  style={{
                    padding: '10px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: '10px 16px',
                    border: 'none',
                    borderRadius: '8px',
                    backgroundColor: '#00C8FF',
                    color: '#000',
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? 'Saving…' : editing ? 'Save changes' : 'Add partner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreferredPartnersSection;
